// /services/metal-plating/ (ProjectServicePage). Replaces the old
// styled-components page (src/pages/services/metal-plating.js) that read its
// copy from Contentful; the slug was already in gatsby-node's skip list.
// GSC Aug–Oct 2026: 15 clicks, 1K impressions. Queries: "3d printing
// prototype service with electroplating" (191 impressions, pos 5.9),
// "electroplating services" (pos 31), "electroplating new york", "copper
// plating process brooklyn ny". Keyword data (US, 2026-11-04):
// "electroplating 3d prints" 1,300/mo, KD 0, CPC $10.88; "metal plating"
// 1,300; "electroplating service(s)" 880. So the title leads with
// "Electroplating 3D Prints" and keeps "Metal Plating".
// Facts: .seo/truth.md "Public metal-plating scope" (a metal surface, not
// solid metal), "Metal-plating review inputs", the trophy turnaround row
// (plating runs 6–8 weeks after model approval) and the props row (metal
// plating over resin; no real chrome or bronze casting). Finishes named are
// the ones the 3D printing planner and gallery captions already publish:
// chrome, gold, bronze. Price: quoted per project.
const {
  pricing,
  reviewedDate,
  studios,
  printingPhotos,
  photo,
} = require('./shared')

const photos = {
  ...printingPhotos,
  drone: photo(
    '2ZxE3gNGhiCygGeEiQUgc4',
    '0723501ce52d91a0a79a2efd8c1c02e5/Metal_plated_drone_top_view.jpg',
    6176,
    4912,
    'Top view of a metal-plated 3D printed drone body with a silver finish',
    'Plated drone body',
    'A printed drone shell with a silver plated finish.'
  ),
  figures: photo(
    '1MDgDwPnHXMQU9v5T0vvUl',
    'ce99baba9f059be6a87337ad2ce75802/Metal-Plating-2.jpg',
    2000,
    1445,
    'Six chrome metal-plated 3D printed human figures in different poses',
    'Chrome-plated figures',
    'One figure in six poses, each printed and plated.'
  ),
  sculptures: photo(
    '7uNvIJpcnrpXGVcQDZvMOB',
    '651a36637cf8d1385197fa6af3c526f0/Metal-Plating-1.jpg',
    2000,
    1335,
    'A row of metal-plated 3D printed sculptures on a workbench',
    'Plated sculptures on the bench',
    'A run of plated pieces lined up after finishing.'
  ),
  emojiHands: photo(
    '4Vx4wxq27gJVfnWf98Oq1u',
    'ba6aee425c56f1b48e41676c29796c54/WechatIMG1652.jpeg',
    1440,
    1080,
    'Two chrome-plated 3D printed praying-hands sculptures photographed in a studio',
    'Chrome praying hands',
    'Printed in resin, then chrome plated.'
  ),
  goldStatue: photo(
    '6gDgNp3fBDYHXpbKhamVbE',
    '242faa0a1aa143f4064885f25f1bb195/3d_printed_prayer_statue_by_mark_kent_full.JPG',
    2048,
    2048,
    'A gold-finished 3D printed statue of a praying figure on a round base',
    'Gold statue',
    'A detailed resin statue finished in gold.'
  ),
  platedChair: photo(
    '2HUFG8LJ63B1ovcJZWVNRD',
    '1d635bd233063cbafd6526027c743125/L1040158.jpg',
    3000,
    2000,
    'A chrome metal-plated sculptural chair on display at an art event',
    'Chrome-plated art furniture',
    'Art furniture printed, prepared and plated to a mirror finish.'
  ),
}

const page = {
  path: '/services/metal-plating/',
  title: 'Electroplating 3D Prints | Metal Plating Service in NYC',
  description:
    'Metal plating and electroplating for 3D prints, artwork, awards and prototypes: chrome, gold and other metal finishes over resin, from our New York studios.',
  heading: 'Metal Plating Service for 3D Prints and Artwork',
  eyebrow: 'Electroplating 3D prints · New York',
  answer:
    'PrintAWorld metal plates 3D prints, artwork, awards, props and prototypes in chrome, gold and other metal finishes. We can print the piece for you in resin and plate it, or review a print or object you already have. Plating adds a real metal surface over the print; it doesn’t make the piece solid metal. Surface prep makes the finish, so we check the base, sand and prime as needed, then plate. Plated pieces usually take 6–8 weeks after the model is approved, and each job is quoted per project. Our studios are in Queens and Brooklyn, and we ship across the U.S.',
  hero: 'chromeFigures',
  reviewedDate,
  crumbs: [{ name: 'Metal plating' }],
  service: {
    name: 'Metal Plating and Electroplating for 3D Prints',
    serviceType: 'Metal plating for 3D prints and artwork',
    areaServed: [
      { '@type': 'City', name: 'New York' },
      { '@type': 'Country', name: 'United States' },
    ],
    description:
      'Decorative metal plating for 3D-printed objects, artwork, awards, props and prototypes in chrome, gold and other metal finishes, with printing and surface preparation when quoted. Plating adds a metal surface; it does not make the object solid metal.',
  },
  quoteLabel: 'Get a metal plating quote',
}

const intro = {
  eyebrow: 'Metal plating 3D prints',
  heading: 'What is electroplating a 3D print?',
  paragraphs: [
    'Electroplating a 3D print puts a thin layer of real metal over a printed part, so a resin print can look and feel like chrome, gold or bronze. The print underneath stays plastic, which keeps the piece light and lets it take shapes a metal casting couldn’t.',
    'The plate copies every mark on the surface below it, so most of the work happens before plating: removing supports, filling, sanding and priming until the base is smooth. That’s why we usually print the part ourselves in resin. Need the piece printed first? See [SLA resin 3D printing](/services/sla-3d-printing/).',
  ],
}

const glance = {
  heading: 'Our metal plating service at a glance',
  rows: [
    ['Studios', studios],
    [
      'What we plate',
      '3D prints, artwork and sculpture, awards and trophies, props and display pieces, presentation prototypes',
    ],
    ['Finishes', 'Chrome, gold, bronze and other metal finishes'],
    [
      'What plating is',
      'A metal surface over the object. It isn’t solid metal; for that, see metal 3D printing.',
    ],
    [
      'Your part or ours',
      'We print it in resin and plate it, or review a print or object you send',
    ],
    [
      'Surface prep',
      'Support removal, filling, sanding and priming, as quoted',
    ],
    ['Turnaround', 'Plated pieces usually take 6–8 weeks after model approval'],
    ['Price', 'Quoted per project'],
    [
      'How you get a price',
      'Send photos or a 3D file, size, quantity and finish for a written quote',
    ],
    ['Delivery', 'Pickup by appointment, or shipped across the U.S.'],
  ],
}

const work = {
  eyebrow: 'Plated work',
  heading: 'Chrome and gold plated 3D prints from our studio',
  photos: [
    'goldBurger',
    'platedHands',
    'figures',
    'drone',
    'goldStatue',
    'platedChair',
  ],
}

const offer = {
  eyebrow: 'What we plate',
  heading: 'What can be metal plated?',
  index: '02 / THE OBJECTS',
  items: [
    {
      label: 'P1',
      title: 'Trophies and awards',
      body: 'Printed awards in gold or chrome that look cast but weigh less and can take any shape.',
    },
    {
      label: 'P2',
      title: 'Sculpture and artwork',
      body: 'Organic forms and figures where a mirror or gold surface is part of the piece.',
    },
    {
      label: 'P3',
      title: 'Props and replicas',
      body: 'Oversized product replicas, logos and props for events and shoots.',
    },
    {
      label: 'P4',
      title: 'Presentation prototypes',
      body: 'Product samples that need a metal look for a pitch, a shoot or a trade show.',
    },
    {
      label: 'P5',
      title: 'Prints you already have',
      body: 'Send photos first: we check the material and surface and say what prep it needs.',
    },
    {
      label: 'P6',
      title: 'Runs of the same piece',
      body: 'Several copies printed and plated together so they match.',
    },
  ],
}

const contrast = {
  eyebrow: 'Plated or solid?',
  heading: 'Metal plating vs metal 3D printing: which do you need?',
  paragraphs: [
    'Plating gives a printed piece a metal surface. It suits display: awards, sculpture, props and presentation models, where the look matters and the weight doesn’t.',
    'If the part has to be solid metal, for strength, heat or weight, it should be printed in metal. We 3D print solid stainless steel by selective laser melting.',
  ],
  links: [
    ['Metal 3D printing', '/services/metal-3d-printing/'],
    ['Custom 3D printing', '/services/custom-3d-printing/'],
  ],
}

const steps = {
  eyebrow: 'How it works',
  heading: 'How our metal plating service works',
  items: [
    {
      title: 'Send photos or a file',
      body: 'Photos or a 3D file, size, quantity, the base material, the finish you want and your date.',
    },
    {
      title: 'We review and quote',
      body: 'We check whether it can be plated, what prep it needs, and whether we should print it first.',
    },
    {
      title: 'Print and prep',
      body: 'Resin print if needed, then supports off, fill, sand and prime until the surface is smooth.',
    },
    {
      title: 'Plate and deliver',
      body: 'Plating in the approved finish, a final check, then pickup or packed shipping.',
    },
  ],
}

const fit = {
  eyebrow: 'Is it the right fit?',
  heading: 'Is metal plating right for your piece?',
  good: [
    'Display pieces: awards, sculpture, props and presentation models',
    'Resin prints that should look like chrome, gold or bronze',
    'Complex shapes that would be costly to cast in metal',
    'A matched run of plated pieces for an event or a team',
  ],
  other: [
    'Industrial plating to a spec, such as hard chrome or corrosion coatings',
    'Re-chroming car parts or restoring metal hardware: a plating shop does that',
    'Parts that must be solid metal: see metal 3D printing',
    'Cast bronze or solid gold jewelry: a foundry or caster',
  ],
}

const pricing_ = {
  eyebrow: 'Budgeting your piece',
  heading: 'How much does metal plating a 3D print cost?',
  lead: {
    label: 'Metal plating is',
    value: 'Quoted per project',
    note: 'Size, surface prep and finish set the price.',
  },
  rowsHeading: 'What sets the price',
  rows: [
    ['Size and surface area', 'More surface means more plating'],
    [
      'Surface prep',
      'Supports, seams and layer lines take sanding and filling',
    ],
    ['Finish', 'Chrome, gold, bronze or another metal'],
    ['Quantity', 'Matched runs are quoted per piece'],
    ['3D printing the base', `From about $${pricing.printFrom}, no setup fee`],
    ['3D modeling from a sketch', `From $${pricing.modelingFrom}`],
  ],
  note: 'Send photos or a file with the size and finish, and we’ll quote it.',
}

const logistics = {
  eyebrow: 'Pickup & shipping',
  heading: 'Where can you get your plated pieces?',
  paragraphs: [
    'Pick up by appointment at the studio named in your quote, or we pack the plated piece to protect the finish and ship it across the U.S. Sending us a part to plate? Don’t ship it until your quote gives the studio and handling instructions.',
  ],
  aside: {
    eyebrow: 'Have a deadline?',
    title: 'Plating takes the longest.',
    body: 'Plated pieces usually take 6–8 weeks after the model is approved. If your date is tighter, ask about a painted metallic finish instead; your quote confirms the date.',
  },
}

const related = {
  heading: 'Related finishing and printing',
  links: [
    ['Art fabrication', '/services/art-fabrication/'],
    ['SLA resin 3D printing', '/services/sla-3d-printing/'],
    ['Custom 3D printing', '/services/custom-3d-printing/'],
    ['Metal 3D printing', '/services/metal-3d-printing/'],
  ],
}

const faqs = [
  {
    question: 'Can you electroplate 3D prints?',
    answer:
      'Yes. PrintAWorld plates 3D prints, artwork, awards, props and prototypes in chrome, gold and other metal finishes. We usually print the part in resin ourselves so the surface is right, then sand, prime and plate it. You can also send photos of a print you already have for review.',
  },
  {
    question: 'Does metal plating make a 3D print solid metal?',
    answer:
      'No. Plating adds a metal surface over the print; the core stays plastic. For a solid metal part, we 3D print stainless steel by selective laser melting instead.',
  },
  {
    question: 'How much does metal plating cost?',
    answer: `It’s quoted per project. Size, surface prep, finish and quantity set the price. If we print the base, 3D printing starts around $${pricing.printFrom} with no setup fee, and 3D modeling from a sketch starts at $${pricing.modelingFrom}.`,
  },
  {
    question: 'How long does metal plating take?',
    answer:
      'Plated pieces usually take 6–8 weeks after the model is approved. If you need it sooner, ask about a painted metallic finish; your written quote confirms the date.',
  },
  {
    question: 'Which metal finishes can you plate?',
    answer:
      'Chrome, gold and bronze are the most requested, and other metal finishes can be reviewed. Tell us the look you want (mirror or brushed, yellow or rose gold) and we’ll confirm what’s possible in the quote.',
  },
  {
    question: 'Can you plate a print I made myself?',
    answer:
      'Send photos first, with the material and size. Plating shows every layer line and support mark, so we’ll say what prep it needs or whether reprinting it in resin is the better route. Don’t ship the part until your quote gives the studio and instructions.',
  },
  {
    question: 'Is plating durable enough to handle?',
    answer:
      'Plating is a presentation finish for awards, sculpture, props and display pieces. Tell us how the piece will be handled and where it will live, and we’ll recommend a finish and say if plating isn’t the right choice.',
  },
  {
    question: 'Do you offer electroplating in New York?',
    answer:
      'Yes. Our studios are in Glendale, Queens and Brooklyn, by appointment. We ship finished pieces across the U.S.',
  },
]

const checklist = [
  'Photos or a 3D file of the piece',
  'Overall size and quantity',
  'Base material and surface (or “please print it”)',
  'Finish: chrome, gold, bronze…, mirror or brushed',
  'How it will be handled, your date and delivery ZIP',
]

module.exports = {
  key: 'plating',
  page,
  photos,
  intro,
  glance,
  work,
  offer,
  contrast,
  steps,
  fit,
  pricing: pricing_,
  logistics,
  related,
  faqs,
  faqHeading: 'What do people ask about metal plating 3D prints?',
  checklist,
  quote: {
    eyebrow: 'Let’s plate your piece',
    heading: ['Send us the piece.', 'We’ll send a price.'],
    body: 'Photos or a file, the size and the finish you want. We reply with a written quote and next steps.',
  },
}
